import { useCallback, useEffect, useRef } from 'react';
import type { FilterValue } from '../components/ui/tableTemplate/TableTemplateTypes';
import { useFilterState } from './useFilterState';
import { usePaginationState, DEFAULT_TABLE_PAGE_SIZE } from './usePaginationState';
import { useSortState } from './useSortState';

/**
 * Composite hook that wires sort, filter, and pagination state together.
 * Automatically resets to page 1 whenever sort or filter changes.
 *
 * Usage: const { sortState, handleSortChange, filters, debouncedFilters,
 *               setFilter, clearFilter, clearAll, page, limit, setPage, setLimit } = useTableState<MyRow>();
 */
export function useTableState<T>(defaultLimit = DEFAULT_TABLE_PAGE_SIZE) {
  const { sortState, handleSortChange: _handleSortChange } = useSortState<T>();
  const {
    filters,
    debouncedFilters,
    activeFilterKeys,
    setFilter: _setFilter,
    clearFilter: _clearFilter,
    clearAll: _clearAll,
  } = useFilterState<T>();
  const { page, limit, setPage, setLimit, resetPage } = usePaginationState(defaultLimit);

  const handleSortChange = useCallback(
    (key: keyof T) => {
      _handleSortChange(key);
      resetPage();
    },
    [_handleSortChange, resetPage],
  );

  // Reset page when debouncedFilters settles (skip initial mount).
  // This ensures page and debouncedFilters update in the same render,
  // preventing a spurious fetch with stale filters at the reset page.
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    resetPage();
  }, [debouncedFilters, resetPage]);

  const setFilter = useCallback(
    (key: keyof T, value: FilterValue) => {
      _setFilter(key, value);
    },
    [_setFilter],
  );

  const clearFilter = useCallback(
    (key: keyof T) => {
      _clearFilter(key);
    },
    [_clearFilter],
  );

  const clearAll = useCallback(() => {
    _clearAll();
  }, [_clearAll]);

  return {
    // Sort
    sortState,
    handleSortChange,
    // Filter
    filters,
    debouncedFilters,
    activeFilterKeys,
    setFilter,
    clearFilter,
    clearAll,
    // Pagination
    page,
    limit,
    setPage,
    setLimit,
  };
}
